// @flow

type HubspotContext = {
    pageUri: string,
    pageName: string,
    hutk?: string,
};

type HubspotFormField = {
    name: string,
    value: string,
};

type HubspotFormData = {
    submittedAt: number,
    context: HubspotContext,
    fields: HubspotFormField[],
};

/**
 * The list of known hubspot error types.
 * https://legacydocs.hubspot.com/docs/methods/forms/submit_form
 */
type HubspotErrorType =
    | "MAX_NUMBER_OF_SUBMITTED_VALUES_EXCEEDED"
    | "INVALID_EMAIL"
    | "BLOCKED_EMAIL"
    | "REQUIRED_FIELD"
    | "INVALID_NUMBER"
    | "INPUT_TOO_LARGE"
    | "FIELD_NOT_IN_FORM_DEFINITION"
    | "NUMBER_OUT_OF_RANGE"
    | "VALUE_NOT_IN_FIELD_DEFINITION"
    | "INVALID_METADATA"
    | "INVALID_GOTOWEBINAR_WEBINAR_KEY"
    | "INVALID_HUTK"
    | "INVALID_IP_ADDRESS"
    | "INVALID_PAGE_URI"
    | "INVALID_LEGAL_OPTION_FORMAT"
    | "MISSING_PROCESSING_CONSENT"
    | "MISSING_PROCESSING_CONSENT_TEXT"
    | "MISSING_COMMUNICATION_CONSENT_TEXT"
    | "MISSING_LEGITIMATE_INTEREST_TEXT"
    | "DUPLICATE_SUBSCRIPTION_TYPE_ID"
    | "FORM_HAS_RECAPTCHA_ENABLED";

class HubspotError {
    +message: string;

    +errorType: HubspotErrorType;

    constructor(message: string, errorType: HubspotErrorType) {
        this.message = message;
        this.errorType = errorType;
    }

    get realMessage(): string {
        switch (this.errorType) {
            case "INVALID_EMAIL":
                return "This email is not valid";
            case "BLOCKED_EMAIL":
                return "This email has been blocked";
            case "REQUIRED_FIELD":
                return "This field is required";
            default:
                return this.message;
        }
    }

    static parse(item: any): HubspotError {
        const { message, errorType } = item;
        return new HubspotError(message, errorType);
    }
}

export class HubspotErrorResponse extends Error {
    +errors: HubspotError[];

    constructor(message: string, errors: HubspotError[]) {
        super(message);
        this.errors = errors;
    }

    getError(field: string): HubspotError | null {
        return (
            this.errors.find(error => error.message.includes(`'${field}'`)) ||
            null
        );
    }

    static from(request: XMLHttpRequest): HubspotErrorResponse {
        const isJsonResponse =
            (request.getResponseHeader("Content-Type") || "").indexOf(
                "application/json",
            ) !== -1;
        if (isJsonResponse) {
            const json = JSON.parse(request.response);
            const message = json.message || "No message";
            const errorsField = json.errors;
            const errors = Array.isArray(errorsField)
                ? errorsField.map(item => HubspotError.parse(item))
                : [];
            return new HubspotErrorResponse(message, errors);
        }

        return new HubspotErrorResponse("No response body", []);
    }
}

export class HubspotResponse {
    +message: string;

    constructor(message: string) {
        this.message = message;
    }

    static from(request: XMLHttpRequest): HubspotResponse {
        const defaultMessage = "Thanks for submitting the form.";
        const isJsonResponse =
            (request.getResponseHeader("Content-Type") || "").indexOf(
                "application/json",
            ) !== -1;
        if (isJsonResponse) {
            const json = JSON.parse(request.response);
            return new HubspotResponse(
                json.inlinedMessage || defaultMessage,
            );
        }

        return new HubspotResponse(defaultMessage);
    }
}

type HubspotFieldMapping = {
    [string]: string,
};

class AbstractHubspotForm {
    +portalId: string;

    +formId: string;

    constructor(portalId: string, formId: string) {
        this.portalId = portalId;
        this.formId = formId;
    }

    static getCookie(name: string): string | null {
        const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
        if (match) {
            return match[2];
        }

        return null;
    }

    /**
     * Gathers additional form data that Hubspot requires.
     *
     * @return {HubspotContext}
     */
    static getContext(): HubspotContext {
        return {
            pageUri: window.location.href,
            pageName: document.title,
        };
    }
}

export class HubspotFormV2 extends AbstractHubspotForm {
    get URL(): string {
        return `https://forms.hubspot.com/uploads/form/v2/${this.portalId}/${this.formId}`;
    }

    static format(value: any): string | Blob {
        if (value instanceof Blob) {
            return value;
        }

        if (typeof value === "object") {
            return JSON.stringify(value);
        }

        return value;
    }

    static toFormData(data: any): FormData {
        const formData = new FormData();
        for (const [key, value] of Object.entries(data)) {
            formData.append(key, this.format(value));
        }
        return formData;
    }

    post(data: FormData): Promise<HubspotResponse> {
        const request = new XMLHttpRequest();
        request.responseType = "application/json";

        return new Promise((resolve, reject) => {
            request.onreadystatechange = function onReadyStateChange() {
                if (request.readyState === XMLHttpRequest.DONE) {
                    const status = request.status;
                    if (status >= 200 && status < 400) {
                        resolve(HubspotResponse.from(request));
                    } else {
                        reject(HubspotErrorResponse.from(request));
                    }
                }
            };
            request.open("POST", this.URL);
            request.send(data);
        });
    }

    submit(data: any): Promise<HubspotResponse> {
        return this.post(
            HubspotFormV2.toFormData({
                ...data,
                hs_context: AbstractHubspotForm.getContext(),
            }),
        );
    }
}

export default class HubspotForm<
    T: { [string]: string },
> extends AbstractHubspotForm {
    /**
     * Field name mappings, used when the internal name differs from the field in hubspot.
     * Example: { name: "firstname" } // when the internal name is "name" and the hubspot name is "firstname".
     *
     * @type {HubspotFieldMapping}
     */
    +fieldMapping: HubspotFieldMapping;

    constructor(
        portalId: string,
        formId: string,
        fieldMapping: HubspotFieldMapping = {},
    ) {
        super(portalId, formId);
        this.fieldMapping = fieldMapping;
    }

    get URL(): string {
        return `https://api.hsforms.com/submissions/v3/integration/submit/${this.portalId}/${this.formId}`;
    }

    static post(url: string, data: HubspotFormData): Promise<HubspotResponse> {
        return new Promise((resolve, reject) => {
            const request = new XMLHttpRequest();
            request.responseType = "application/json";
            request.onreadystatechange = function onReadyStateChange() {
                if (request.readyState === XMLHttpRequest.DONE) {
                    const status = request.status;
                    if (status >= 200 && status < 400) {
                        resolve(HubspotResponse.from(request));
                    } else {
                        reject(HubspotErrorResponse.from(request));
                    }
                }
            };
            request.open("POST", url);
            request.setRequestHeader("Content-Type", "application/json");
            request.send(JSON.stringify(data));
        });
    }

    mapFieldName(name: string): string {
        return this.fieldMapping[name] || name;
    }

    createFormData(data: T): HubspotFormData {
        const fields = Object.entries(data).map(([key, value]) => ({
            name: this.mapFieldName(key),
            value: String(value),
        }));
        return {
            submittedAt: new Date().getTime(),
            context: AbstractHubspotForm.getContext(),
            fields: fields,
        };
    }

    submit(data: T): Promise<HubspotResponse> {
        const formData = this.createFormData(data);
        return HubspotForm.post(this.URL, formData);
    }
}
